// src/pages/DeveloperDetailsPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiMapPin } from 'react-icons/fi';
import { FaBed } from 'react-icons/fa';
import axiosInstance from '../services/BaseUrl';
import { getDeveloperDetail } from '../services/Api';
import { GlobalContext } from '../GlobalContext';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';

export default function DeveloperDetailsPage() {
  const { idOrSlug } = useParams();
  const [developer, setDeveloper] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { globalVar } = useContext(GlobalContext);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    message: '',
    slug: '',
    developer_name: '',
  });

  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axiosInstance.post('/contact-us/', form, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (res.data.success) {
        setForm({
          name: '',
          email: '',
          phone: '',
          role: '',
          message: '',
          slug: '',
          developer_name: '',
        });
        navigate('/thank-you');
      } else {
        alert(res.data.message || 'Failed to send message.');
      }
    } catch (err) {
      console.error(err);
      alert('An error occurred while sending your message.');
    } finally {
      setSending(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getDeveloperDetail(idOrSlug);
      if (response.success) {
        const dev_state = response.data.developer;
        setDeveloper(dev_state);
        setProjects(response.data.properties);
        if (!isNaN(idOrSlug)) {
          navigate(`/developer-detail/${dev_state.slug}`, { replace: true });
        }
      } else {
        setError('Developer Details not found');
      }
    } catch (err) {
      console.error('Error in DeveloperDetailsPage:', err);
      setError(err.message || 'Error fetching data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [idOrSlug]);

  const getImageUrl = (path) => `${globalVar}/${path}`;

  if (loading) return <p className="p-8 text-center">Loading...</p>;
  if (error) return <p className="p-8 text-center text-red-600">{error}</p>;
  if (!developer)
    return <p className="p-8 text-center text-red-600">Developer not found.</p>;

  return (
    <div className="min-h-screen pt-24 bg-gray-50">
      {/* Developer Info Card */}
      <div className="max-w-7xl mx-auto mb-12 bg-white p-8 rounded-xl shadow">
        <div className="flex items-center space-x-6">
          {developer.logo || developer.logo_url ? (
            <img
              src={getImageUrl(developer.logo || developer.logo_url)}
              alt={developer.name}
              className="w-24 h-24 object-contain rounded"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-200 rounded flex items-center justify-center text-xl font-bold text-gray-400">
              {developer.name.charAt(0)}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{developer.name}</h1>
            {developer.location && (
              <div className="flex items-center text-gray-600 mt-2">
                <FiMapPin className="mr-1" /> {developer.location}
              </div>
            )}
          </div>
        </div>
        <p className="mt-4 text-gray-700">{developer.description}</p>
      </div>

      {/* Main Content */}
      <section className="px-6 sm:px-8 lg:px-12 pb-16">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10">
          {/* LEFT: Properties */}
          <div className="flex-1">
            <h2 className="text-2xl font-semibold mb-6">
              Properties by {developer.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
              {projects.map((property) => (
                <Link
                  key={property.slug}
                  to={`/property-detail/${property.slug}`}
                  className="block bg-white rounded-xl shadow hover:shadow-lg overflow-hidden"
                >
                  <div className="relative">
                    <img
                      src={
                        property.images?.[0]?.image
                          ? property.images[0].image.startsWith('/media')
                            ? `${globalVar}${property.images[0].image}`
                            : property.images[0].image
                          : '/placeholder.jpg'
                      }
                      alt="Property"
                      className="h-48 w-full object-cover"
                    />
                    <span className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs font-medium px-2 py-1 rounded">
                      {property.category?.name || 'Category'}
                    </span>
                    {property.delivery_date && (
                      <span className="absolute top-2 right-2 bg-white text-black text-xs font-semibold px-2 py-1 rounded shadow">
                        {new Date(property.delivery_date).toISOString().split('T')[0]}
                      </span>
                    )}
                                                     {property.sales_status === 'Sold Out' && (
                <span className="absolute bottom-2 right-2 bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-md z-10">
                  Sold Out
                </span>
              )}
                  </div>
                  <div className="p-4 space-y-1">
                    <div className="flex justify-between items-center text-sm font-semibold text-gray-800">
                      <span>AED {Math.round(property.price).toLocaleString()}</span>
                      <span className="text-xs text-gray-500">
                        {
                          [...new Set(property.units?.map((u) => u.unit_type))]
                            .filter(Boolean)
                            .join(', ') || property.property_type?.name || ''
                        }
                      </span>
                    </div>
                    <h3 className="text-md font-semibold text-gray-900 line-clamp-1">
                      {property.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {property.location?.emirate}
                      {property.community?.name
                        ? ` / ${property.community.name}`
                        : ''}
                    </p>
                    <div className="flex justify-between items-center text-sm mt-2 text-gray-600">
                      <span className="flex items-center gap-1">
                        <FaBed />
                        {[...new Set(property.units?.map((u) => u.rooms))]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                      <span>{property.area_sqft} sqft</span>
                    </div>
                  </div>
                </Link>
              ))}
              {projects.length === 0 && (
                <div className="col-span-full text-center py-10">
                  <p className="text-gray-600">No properties found.</p>
                </div>
              )}
            </div>
          </div>

                

        </div>
      </section>

                <section className="max-w-7xl mx-auto px-4 pb-12">
          <ContactForm
  propertyTitle={developer.name}
  propertySlug={developer.slug}
  developerName={developer.name}
  customMessage=""  // Optionally pass a custom message if needed
  isPlaceholderMessageEnabled={false}  // No placeholder for this page
/>

          
          
          
          
                </section>

      <Footer />
    </div>
  );
}